<template>
  <div class="post_create">
    <div class="post_create__header">
      <button type="button" class="post_create__back" @click="onBack">‹</button>
      <span>发帖</span>
      <i class="post_create__header_placeholder" aria-hidden="true" />
    </div>

    <!-- 圈子卡片：圈子信息 + 发布按钮 -->
    <div class="post_create__group">
      <div class="post_create__group_main">
        <div class="post_create__group_icon">
          <img v-if="groupIcon" :src="groupIcon" alt="" />
        </div>
        <span class="text_ellipsis">{{ groupName || '论坛' }}</span>
      </div>
      <button
        type="button"
        class="post_create__publish"
        :class="{ 'post_create__publish--disabled': !publishEnabled }"
        :disabled="!publishEnabled"
        @click="onPublish"
      >
        {{ submitting ? '发布中…' : '发布' }}
      </button>
    </div>

    <!-- 标题输入 -->
    <div class="post_create__field">
      <input
        :value="title"
        type="text"
        placeholder="请输入标题（必填）"
        @input="onTitleInput($event.target.value)"
      />
      <span :class="{ danger: titleAtLimit }">{{ titleLen }}/50</span>
    </div>

    <!-- 正文输入 -->
    <div class="post_create__textarea">
      <textarea
        :value="content"
        placeholder="写下你想说的…"
        @input="onContentInput($event.target.value)"
      />
      <div class="post_create__counter">
        <span :class="{ danger: contentAtLimit }">{{ contentLen }}/10000</span>
      </div>
    </div>

    <!-- 上传图片/视频 -->
    <div class="post_create__media_section">
      <div v-if="images.length" class="post_create__media">
        <div v-for="item in images" :key="item.uid" class="post_create__thumb">
          <img :src="item.preview" alt="" />
          <button type="button" @click="removeImage(item.uid)">✕</button>
        </div>
      </div>

      <div v-if="video" class="post_create__video_wrap">
        <div class="post_create__thumb post_create__thumb--video">
          <video :src="video.preview" />
          <button type="button" @click="removeVideo">✕</button>
        </div>
      </div>

      <!-- 图片 / 视频分开入口，互斥时禁用 -->
      <div class="post_create__toolbar">
        <button
          type="button"
          class="post_create__toolbar_btn"
          :class="{ post_create__toolbar_btn_disabled: imagePickDisabled }"
          :disabled="imagePickDisabled"
          @click="pickImages"
        >
          <van-icon class="post_create__toolbar_icon" name="photo-o" />
          <span>图片</span>
        </button>
        <button
          type="button"
          class="post_create__toolbar_btn"
          :class="{ post_create__toolbar_btn_disabled: videoPickDisabled }"
          :disabled="videoPickDisabled"
          @click="pickVideo"
        >
          <van-icon class="post_create__toolbar_icon" name="video-o" />
          <span>视频</span>
        </button>
      </div>
    </div>

    <input
      ref="imageInput"
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp"
      multiple
      class="post_create__file"
      @change="onImagesPicked"
    />
    <input
      ref="videoInput"
      type="file"
      accept="video/*"
      class="post_create__file"
      @change="onVideoPicked"
    />

    <van-dialog
      v-model="showLeaveDialog"
      title="确认放弃编辑？"
      message="已输入内容将不会保存"
      show-cancel-button
      cancel-button-text="继续编辑"
      confirm-button-text="放弃"
      @confirm="confirmLeave"
      @cancel="cancelLeave"
    />
  </div>
</template>

<script>
import { Dialog, Toast, Icon } from 'vant';
import { sendPosts, uploadForumFile } from '@/api/forum';
import { markForumPostCreateSuccess } from '@/utils/forum/groupReturnAction';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';

const TITLE_MAX = 50;
const CONTENT_MAX = 10000;
const IMAGE_MAX_COUNT = 9;
const IMAGE_MAX_SIZE = 10 * 1024 * 1024;
const VIDEO_MAX_SIZE = 10 * 1024 * 1024;
const IMAGE_ACCEPT_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

export default {
  name: 'ForumPostCreate',
  mixins: [trackingMixin],
  components: {
    'van-dialog': Dialog.Component,
    'van-icon': Icon,
  },
  data() {
    return {
      title: '',
      content: '',
      images: [],
      video: null,
      submitting: false,
      skipLeaveGuard: false,
      showLeaveDialog: false,
      uidCounter: 0,
      bootstrappedGroupId: '',
    };
  },
  computed: {
    groupId() {
      return String(this.$route.query.groupId || '0');
    },
    groupName() {
      return String(this.$route.query.groupName || '');
    },
    groupIcon() {
      return String(this.$route.query.groupIcon || '');
    },
    titleLen() {
      return Array.from(this.title).length;
    },
    contentLen() {
      return Array.from(this.content).length;
    },
    titleAtLimit() {
      return this.titleLen >= TITLE_MAX;
    },
    contentAtLimit() {
      return this.contentLen >= CONTENT_MAX;
    },
    publishEnabled() {
      return this.title.replace(/[\s\u3000]/g, '').length > 0 && !this.submitting;
    },
    hasAnyInput() {
      return this.title.trim() || this.content.trim() || this.images.length || this.video;
    },
    imagePickDisabled() {
      return this.video !== null;
    },
    videoPickDisabled() {
      return this.images.length > 0;
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.skipLeaveGuard || !this.hasAnyInput) {
      next();
      return;
    }
    this.showLeaveDialog = true;
    next(false);
  },
  activated() {
    const currentGroupId = this.groupId;
    // 切换圈子时重置草稿，避免发到错误圈子
    if (this.bootstrappedGroupId && this.bootstrappedGroupId !== currentGroupId) {
      this.resetForm({ keepLeaveGuard: true });
    }
    this.bootstrappedGroupId = currentGroupId;
    this.skipLeaveGuard = false;
    this.showLeaveDialog = false;
    this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_page_show, {
      page_type: '发帖',
      group_id: this.groupId,
      group_name: this.groupName,
    });
  },
  beforeDestroy() {
    this.releaseMediaPreviews();
  },
  methods: {
    onBack() {
      if (this.skipLeaveGuard || !this.hasAnyInput) {
        this.$router.back();
        return;
      }
      this.showLeaveDialog = true;
    },
    confirmLeave() {
      this.resetForm({ keepLeaveGuard: true });
      this.$router.back();
    },
    cancelLeave() {
      this.showLeaveDialog = false;
    },
    genUid() {
      this.uidCounter += 1;
      return `m_${Date.now()}_${this.uidCounter}`;
    },
    onTitleInput(val) {
      this.title = Array.from(String(val).replace(/[\r\n]/g, '')).slice(0, TITLE_MAX).join('');
    },
    onContentInput(val) {
      this.content = Array.from(String(val)).slice(0, CONTENT_MAX).join('');
    },
    isValidImageExt(file) {
      const name = (file.name || '').toLowerCase();
      const dotIdx = name.lastIndexOf('.');
      const ext = dotIdx >= 0 ? name.slice(dotIdx + 1) : '';
      if (ext && IMAGE_ACCEPT_EXT.indexOf(ext) >= 0) return true;
      const mime = (file.type || '').toLowerCase();
      return /^image\/(jpeg|png|gif|webp)$/.test(mime);
    },
    pickImages() {
      if (this.imagePickDisabled) return;
      if (this.images.length >= IMAGE_MAX_COUNT) {
        Toast(`最多上传 ${IMAGE_MAX_COUNT} 张图片`);
        return;
      }
      this.$refs.imageInput.click();
    },
    pickVideo() {
      if (this.videoPickDisabled || this.video) return;
      this.$refs.videoInput.click();
    },
    onImagesPicked(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (this.video) return;
      const remain = IMAGE_MAX_COUNT - this.images.length;
      if (remain <= 0) {
        Toast(`最多上传 ${IMAGE_MAX_COUNT} 张图片`);
        return;
      }
      const accepted = files.slice(0, remain);
      if (files.length > remain) {
        Toast(`最多上传 ${IMAGE_MAX_COUNT} 张图片`);
      }
      accepted.forEach((file) => {
        if (!this.isValidImageExt(file)) {
          Toast('仅支持 JPG / PNG / GIF / WebP 图片');
          return;
        }
        if (file.size > IMAGE_MAX_SIZE) {
          Toast('图片大小不能超过 10MB');
          return;
        }
        this.images.push({ uid: this.genUid(), file, preview: URL.createObjectURL(file), url: '' });
      });
    },
    onVideoPicked(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file || this.images.length > 0) return;
      if (file.size > VIDEO_MAX_SIZE) {
        Toast('视频大小不能超过 10MB');
        return;
      }
      this.video = { uid: this.genUid(), file, preview: URL.createObjectURL(file), url: '' };
    },
    revokePreview(preview) {
      if (preview && preview.indexOf('blob:') === 0) {
        URL.revokeObjectURL(preview);
      }
    },
    releaseMediaPreviews() {
      this.images.forEach((item) => this.revokePreview(item.preview));
      if (this.video) this.revokePreview(this.video.preview);
    },
    resetFileInputs() {
      if (this.$refs.imageInput) this.$refs.imageInput.value = '';
      if (this.$refs.videoInput) this.$refs.videoInput.value = '';
    },
    resetForm({ keepLeaveGuard = false } = {}) {
      this.releaseMediaPreviews();
      this.title = '';
      this.content = '';
      this.images = [];
      this.video = null;
      this.submitting = false;
      this.skipLeaveGuard = keepLeaveGuard;
      this.showLeaveDialog = false;
      this.uidCounter = 0;
      this.resetFileInputs();
    },
    removeImage(uid) {
      const removed = this.images.find((item) => item.uid === uid);
      if (removed) this.revokePreview(removed.preview);
      this.images = this.images.filter((item) => item.uid !== uid);
    },
    removeVideo() {
      if (this.video) this.revokePreview(this.video.preview);
      this.video = null;
    },
    async uploadFile(file) {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const res = await uploadForumFile(formData);
      return res.data;
    },
    async onPublish() {
      if (!this.publishEnabled) return;
      this.submitting = true;
      try {
        let detailType = 0;
        let detailList = [];
        if (this.video) {
          detailType = 1;
          const videoUrl = await this.uploadFile(this.video.file);
          detailList = [{ videoUrl }];
        } else if (this.images.length) {
          detailType = 2;
          detailList = await Promise.all(
            this.images.map(async (item) => ({ picUrl: await this.uploadFile(item.file) })),
          );
        }
        const res = await sendPosts({
          groupId: this.groupId,
          title: this.title.trim(),
          content: this.content,
          detailType,
          detailList,
        });
        const data = (res && res.data) || {};
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_save_post_click, {
          page_type: '发帖页',
          post_id: data.postsId || data.postId || '',
          post_title: this.title.trim(),
          group_id: this.groupId,
          group_name: this.groupName,
        });
        Toast.success('发布成功，审核通过后将展示');
        this.resetForm({ keepLeaveGuard: true });
        markForumPostCreateSuccess(this.groupId);
        this.$router.replace({ name: 'forum-group', params: { groupId: this.groupId } });
      } catch (error) {
        // 接口失败提示由 axios 拦截器统一处理
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.post_create {
  min-height: 100vh;
  background: #f9fafb;
}

.post_create__header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 14px;
  background: #fff;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.post_create__back {
  width: 58px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #333;
  font-size: 30px;
  line-height: 26px;
  text-align: left;
}

.post_create__header_placeholder {
  width: 58px;
  flex-shrink: 0;
}

.post_create__file {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.post_create__group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
}

.post_create__group_main {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.post_create__group_icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  background: #e5e7eb;
  flex-shrink: 0;
}

.post_create__group_icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.post_create__group_main span {
  margin-left: 10px;
  color: #111827;
  font-size: 16px;
  font-weight: 500;
}

.post_create__publish {
  flex-shrink: 0;
  padding: 0 20px;
  height: 36px;
  border: 0;
  border-radius: 18px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  background: #ef4444;
}

.post_create__publish--disabled {
  background: #fecaca;
  cursor: not-allowed;
}

.post_create__field,
.post_create__textarea {
  background: #fff;
  padding: 14px 16px;
  border-bottom: 1px solid #f2f2f2;
}

.post_create__field {
  display: flex;
  align-items: center;
}

.post_create__field input,
.post_create__textarea textarea {
  flex: 1;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  border: 0;
  outline: none;
  color: #111827;
  font-size: 16px;
  line-height: 24px;
  background: transparent;
  padding: 0;
}

.post_create__field input::placeholder,
.post_create__textarea textarea::placeholder {
  color: #9ca3af;
}

.post_create__field span {
  margin-left: 8px;
  flex-shrink: 0;
  color: #9ca3af;
  font-size: 12px;
  line-height: 24px;
}

.post_create__textarea textarea {
  min-height: 160px;
  resize: none;
}

.post_create__counter {
  display: flex;
  justify-content: flex-end;
}

.post_create__counter span {
  color: #9ca3af;
  font-size: 12px;
}

.danger {
  color: #ef4444 !important;
}

.post_create__media_section {
  background: #fff;
  padding: 16px 16px 24px;
}

.post_create__media {
  display: flex;
  overflow-x: auto;
  padding-bottom: 12px;
  -webkit-overflow-scrolling: touch;
}

.post_create__media::-webkit-scrollbar {
  display: none;
}

.post_create__video_wrap {
  padding-bottom: 12px;
}

.post_create__thumb {
  position: relative;
  width: 80px;
  height: 80px;
  margin-right: 8px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  flex-shrink: 0;
}

.post_create__thumb img,
.post_create__thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.post_create__thumb button {
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  border: 0;
  border-bottom-left-radius: 8px;
  color: #fff;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  padding: 0;
  background: rgba(0, 0, 0, 0.6);
}

.post_create__toolbar {
  display: flex;
  align-items: center;
}

.post_create__toolbar_btn {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 14px;
  line-height: 20px;
  color: #333333;
}

.post_create__toolbar_btn + .post_create__toolbar_btn {
  margin-left: 24px;
}

.post_create__toolbar_icon {
  margin-right: 4px;
  font-size: 16px;
  line-height: 1;
}

.post_create__toolbar_btn_disabled {
  color: #cccccc;
  cursor: not-allowed;
}
</style>
